import { useContext, useRef, useState, useEffect } from 'react';
import beTwMerge from '@/utils/beTwMerge';
import CarouselContext from '@/context/CarouselContext';
import type { ICarouselCustomStyles } from './Carousel';
import type { IVideoItem } from './types';

interface ICarouselItemProps {
  item: IVideoItem;
  customItemStyles?: ICarouselCustomStyles;
  isGrid?: boolean;
  isActive?: boolean;
}

export default function CarouselItemVideo({ item, customItemStyles, isGrid = false, isActive }: ICarouselItemProps) {
  const { centerText } = useContext(CarouselContext)!;
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [activeOnce, setActiveOnce] = useState(false);

  const handleImageClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  useEffect(() => {
    if (isActive) {
      setActiveOnce(true);
    }
  }, [isActive]);

  return (
    <div
      className={beTwMerge(
        'tw-w-full tw-pr-0 tw-pl-0 tw-inline-block tw-group',
        isGrid && 'md:tw-pl-0 lg:tw-pl-0 md:tw-inline-table'
      )}
    >
      <div className={beTwMerge('tw-relative tw-flex', customItemStyles?.container)}>
        <div
          className={beTwMerge(
            'tw-relative tw-z-10 [transition:opacity_500ms_linear] tw-opacity-0 tw-will-change-[opacity]',
            activeOnce && 'tw-opacity-100'
          )}
        >
          <video
            ref={videoRef}
            className="tw-col-span-full tw-max-w-full tw-w-full tw-object-fill tw-aspect-square"
            playsInline
            preload="metadata"
            poster={item.posterSrc}
            loop
            muted
            autoPlay={isPlaying}
          >
            <source src={`${item.videoSrc}#t=0.001`} type="video/mp4" />
          </video>
          <img
            className={`tw-absolute tw-top-1/2 tw-left-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 !tw-w-[90px] !tw-h-[90px] tw-cursor-pointer ${
              isPlaying ? 'tw-opacity-0' : 'tw-opacity-100'
            }`}
            src={item.overlaySrc}
            onClick={handleImageClick}
            alt="pause/play"
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
            role="button"
          />
        </div>
        <div
          className={beTwMerge('tw-absolute tw-inset-x-0 tw-inset-y-0 tw-bg-color-grey-50 tw-pointer-events-none')}
        />
      </div>

      <a
        href={item.link}
        className={beTwMerge(
          'hover:tw-no-underline focus:tw-outline-none',
          item.link ? 'hover:tw-text-color-[#599d8e]' : 'hover:tw-text-color-black'
        )}
      >
        {item.name && (
          <h4
            className={beTwMerge(
              'tw-no-underline tw-mt-6 tw-mb-0 tw-font-medium',
              centerText ? 'tw-text-center' : 'tw-text-left',
              item.link ? 'tw-underline' : ''
            )}
          >
            {item.name}
          </h4>
        )}
        {item.description && (
          <div
            className={beTwMerge(
              'tw-font-book tw-mt-2 tw-mb-0 !tw-text-color-black [&>p]:tw-mb-0',
              centerText ? 'tw-text-center' : 'tw-text-left'
            )}
            dangerouslySetInnerHTML={{ __html: item.description }}
          />
        )}
      </a>
    </div>
  );
}
